import { calculateShipping as AlliedcalculateShipping } from "./allied";
import { calculateShipping as PostcalculateShipping } from "./aupost";
import { calculateShipping as AramexcalculateShipping } from "./aramex";
import { calculateShipping as DFEcalculateShipping } from "./dfe";
import { calculateShipping as SYDFEcalculateShipping } from "./sydfe";
import { calculateShipping as HuntercalculateShipping } from "./hunter";
import { calculateShipping as TNTcalculateShipping } from "./tnt";
import { calculateShipping as IMilecalculateShipping } from "./imile";

import orders from "./mockOrders.json";

import largeProductFreePostcodes from "./data/largeFreePostcodes.json";
import allPostcodes from "./data/allPostcodes.json";
import levy from "./data/levy.json";

// Function to count orders by postcode
const countOrdersByPostcode = (orders) => {
  const postcodeCounts = {};

  orders.forEach((order) => {
    const { postcode } = order;
    if (postcodeCounts[postcode]) {
      postcodeCounts[postcode]++;
    } else {
      postcodeCounts[postcode] = 1;
    }
  });

  return postcodeCounts;
};

const setWeight = (length, width, height, inputWeight) => {
  const calcWeight = (length * width * height) / 4000;
  return Math.max(calcWeight, inputWeight);
};

/**
 * 判断免邮区类型 (匹配 Excel 输出表格公式)
 * Excel: =IF(AND(MAX(B2:D2)<=100, B5<=5), SUM(小件免邮区!M:M),
 *           IF(AND(MAX(B2:D2)<=119, B5<=30), SUM(大件免邮区!M:M), "无免邮区"))
 * B5 = 计费重量 = MAX(体积重, 实际重量)
 *
 * @returns {"all" | "large" | "none"}
 */
const getFreeZoneType = (length, width, height, inputWeight) => {
  const maxDim = Math.max(length, width, height);
  const volumetricWeight = (length * width * height) / 4000;
  const chargeableWeight = Math.max(volumetricWeight, inputWeight);

  if (maxDim <= 100 && chargeableWeight <= 5) {
    return "all";
  } else if (maxDim <= 119 && chargeableWeight <= 30) {
    return "large";
  } else {
    return "none";
  }
};

const getShippingValue = (length, width, height, inputWeight) => {
  const ordersCountTable = countOrdersByPostcode(orders);

  let shippingValue = [];
  const weight = setWeight(length, width, height, inputWeight);
  const freeZoneType = getFreeZoneType(length, width, height, inputWeight);

  if (weight > 0 && ordersCountTable) {
    // 根据免邮区类型选择免邮区邮编列表
    let freePostcodes = null;
    if (freeZoneType === "all") {
      freePostcodes = allPostcodes;
    } else if (freeZoneType === "large") {
      freePostcodes = largeProductFreePostcodes;
    }
    // freeZoneType === "none" 时 freePostcodes 为 null，不计算免邮区

    // All orders are from postcodes beyond our free shipping postcode, for example we can't count order from postcode 110.
    const ordersCountTableMostZone = allPostcodes.reduce((acc, curr) => {
      const key = curr.postcode; // Extract the `postcode` value from the object
      if (ordersCountTable.hasOwnProperty(key)) {
        acc[key] = ordersCountTable[key]; // Add matching key-value pair to result object
      }
      return acc;
    }, {});

    const ordersCountTableFreeZone = freePostcodes
      ? freePostcodes.reduce((acc, curr) => {
          const key = curr.postcode;
          if (ordersCountTable.hasOwnProperty(key)) {
            acc[key] = ordersCountTable[key];
          }
          return acc;
        }, {})
      : {};

    // First pass: calculate shipping fees for all postcodes to determine which are valid
    const shippingByPostcode = [];
    allPostcodes.forEach((obj) => {
      const postcode = obj.postcode;

      // Apply GST (×1.1) to all shipping costs
      const allied = AlliedcalculateShipping(postcode, weight, length) * 1.1;
      // AU Post use input weight and dimensions to calculate with levy markup
      const aupostResult = PostcalculateShipping(postcode, inputWeight, length, width, height, levy.aupost);
      const aupost = aupostResult.error ? 999999 : aupostResult.cost * 1.1;
      const aramex = AramexcalculateShipping(postcode, inputWeight, length, width, height, levy.aramex, 'standard') * 1.1;
      const dfe = DFEcalculateShipping(postcode, inputWeight, length, width, height) * 1.1;
      const sydfe = SYDFEcalculateShipping(postcode, inputWeight, length, width, height) * 1.1;
      const hunter = HuntercalculateShipping(postcode, weight, length) * 1.1;
      const tnt = TNTcalculateShipping(postcode, weight, length, width, height) * 1.1;
      const imile = IMilecalculateShipping(postcode, inputWeight, length, width, height) * 1.1;

      const minShippingFee = Math.min(allied, aupost, aramex, dfe, sydfe, hunter, tnt, imile);
      const isValid = parseFloat(minShippingFee) < 9999;

      shippingByPostcode.push({
        postcode,
        allied,
        aupost,
        aramex,
        dfe,
        sydfe,
        hunter,
        tnt,
        imile,
        minShippingFee,
        isValid,
      });
    });

    // Calculate sumOrdersAll only from postcodes where at least one carrier can deliver
    // This matches Excel behavior: SUMPRODUCT only includes rows with valid shipping fees
    let sumOrdersAll = 0;
    let sumOrdersAllFreeZone = 0;
    shippingByPostcode.forEach(({ postcode, isValid }) => {
      if (isValid && ordersCountTableMostZone[postcode]) {
        sumOrdersAll += ordersCountTableMostZone[postcode];
      }
      if (isValid && ordersCountTableFreeZone[postcode]) {
        sumOrdersAllFreeZone += ordersCountTableFreeZone[postcode];
      }
    });

    // Second pass: calculate percentages and rates using valid-only totals
    shippingByPostcode.forEach(({ postcode, allied, aupost, aramex, dfe, sydfe, hunter, tnt, imile, minShippingFee, isValid }) => {
      // get percentage from all orders in current postcode (only among valid postcodes)
      const allOrdersPercentage = (ordersCountTableMostZone[postcode] && sumOrdersAll > 0)
        ? ordersCountTableMostZone[postcode] / sumOrdersAll
        : 0;

      // get percentage from free shipping zone orders in current postcode (only among valid postcodes)
      const ordersPercentage = (ordersCountTableFreeZone[postcode] && sumOrdersAllFreeZone > 0)
        ? ordersCountTableFreeZone[postcode] / sumOrdersAllFreeZone
        : 0;

      shippingValue.push({
        postcode,
        allied,
        aupost,
        aramex,
        dfe,
        sydfe,
        hunter,
        tnt,
        imile,
        minShippingFee,
        allOrders: ordersCountTableMostZone[postcode],
        allOrdersPercentage,
        allOrdersRate: isValid
          ? parseFloat(allOrdersPercentage) * parseFloat(minShippingFee)
          : 0,
        ordersFreeZone: ordersCountTableFreeZone[postcode],
        ordersPercentage,
        freeZoneOrdersRate: isValid
          ? parseFloat(ordersPercentage) * parseFloat(minShippingFee)
          : 0,
      });
    });

    return shippingValue;
  }
};

export const getFreeZoneShipping = (length, width, height, inputWeight) => {
  const freeZoneType = getFreeZoneType(length, width, height, inputWeight);
  if (freeZoneType === "none") {
    return "无免邮区";
  }

  const shippingValue = getShippingValue(length, width, height, inputWeight);

  return shippingValue.reduce((acc, curr) => {
    return acc + curr.freeZoneOrdersRate;
  }, 0);
};

export const getAllShipping = (length, width, height, inputWeight) => {
  const shippingValue = getShippingValue(length, width, height, inputWeight);

  return shippingValue.reduce((acc, curr) => {
    return acc + curr.allOrdersRate; // Add the `postcode` value to the accumulator
  }, 0);
};
